import * as React from 'react';
import { useRouteMatch } from 'react-router';

import { DrawerContext, Grid, ModalContext } from '@notino/react-styleguide';
import { Roles } from '@notino/shared/__generated__/globalSPATypes';
import { AdditionalServicesAvailability } from '@notino/web-tracking';

import { ContentWrapper } from '@components';
import ErrorBoundary from '@components/ErrorBoundary';
import { RenderInView } from '@components/RenderInView';
import { AdminPanel } from '@containers/AdminPanel/loadable';
import { usePriceLogic } from '@containers/ProductDetailContainer/ProductDetail/hooks/usePriceLogic';
import { dispatchTrackingEvent } from '@context/tracking/utils';
import useFeatureSwitches from '@hooks/useFeatureSwitches';
import { TabSwitcherProvider } from '@sharedComponents/TabSwitcher/context/TabSwitcherContext';

import { getProductView } from '../queries/__generated__/getProductView';

import { BlogArticles } from './BlogArticles';
import { Breadcrumb } from './Breadcrumb';
import { Header } from './Header';
import { HeadMeta } from './HeadMeta';
import { useDispatchProductsShowEvent } from './hooks/useDispatchProductsShowEvent';
import { useInjectBazaarvoice } from './hooks/useDynamicCatalogCollection';
import { useModifaceFns } from './hooks/useModiface';
import { useModifaceVariants } from './hooks/useModifaceVariants';
import { useNewPdDesignEnabled } from './hooks/useNewPdDesignEnabled';
import { useRegisterNotinoApi } from './hooks/useRegisterNotinoApi';
import { useTrackProductUrlChange } from './hooks/useTrackProductUrlChange';
import { LastVisitedProducts } from './LastVisitedProducts';
import { ProductBaseInfo } from './ProductBaseInfo';
import { DeliveriesDisplayMode } from './ProductBaseInfo/DeliveryTime';
import {
  ProductDetailContextProvider,
  useProductDetailContext,
} from './ProductDetailContext';
import { ProductImageGallery } from './ProductImageGallery';
import { BrandLogo } from './ProductImageGallery/BrandLogo';
import { isLogoForBrandAvailable } from './ProductImageGallery/BrandLogo/supported-brands';
import { ProductJsonLd } from './ProductJsonLd';
import { ProductTabContent } from './ProductTabContent';
import { RoutineOrRelatedProducts } from './RoutineOrRelatedProducts';
import {
  GalleryWrapper,
  MainInfoWrapper,
  ShowOnDesktop,
  ShowOnMobile,
  TopSection,
  ProductTabContentGrid,
  PDViewPort,
} from './styled';
import {
  trackItemInExponea,
  trackPageReload,
  getVariantDescription,
  getLoyaltyDiscountState,
  isGiftAvailable,
} from './utils';
import { VariantName } from './VariantName';

export interface IProductDetailRouteParams {
  productUrl: string;
  variantUrl?: string;
}

interface IPDProps {
  PDView: getProductView;
  rcKey: string;
  customRcKey: string;
  customVersionKey: string;
}

const ProductDetailComponent = ({
  rcKey,
  customRcKey,
  PDView: {
    productDetailByMasterId: [product],
    currentCurrency,
    currentLocale,
    Settings: { imageDomainPath, deliveriesDisplayMode },
    user,
  },
  customVersionKey,
}: IPDProps) => {
  const mounted = React.useRef<boolean>(false);
  const match = useRouteMatch<IProductDetailRouteParams>();
  const newPdDesignEnabled = useNewPdDesignEnabled();

  const {
    modifaceDesktopEnabled,
    modifaceMobileEnabled,
    reviewsOnServerIds,
    databreakersIntegration,
  } = useFeatureSwitches();

  useInjectBazaarvoice(product);

  const {
    additionalServices,
    engravingAvailable,
    tryItFirstAvailable,
    currentVariant: variant,
  } = useProductDetailContext();

  const modifaceVariants = useModifaceVariants(product.variants);

  useDispatchProductsShowEvent(product.variants, {
    section: 'product_detail',
  });

  const {
    detectWebcamAndTrackProductDetailView,
    handleOpenModifaceModal,
    hasWebcam,
  } = useModifaceFns(product, variant, modifaceVariants);

  const giftAvailable = isGiftAvailable(product.campaigns);

  const loyaltyDiscount = getLoyaltyDiscountState(variant.customerBenefits);

  useRegisterNotinoApi(variant.productCode);

  useTrackProductUrlChange(product, variant);

  const { rrpShown, minimalPriceShown } = usePriceLogic(variant);

  React.useEffect(() => {
    dispatchTrackingEvent({
      event: 'page_view',
      page: {
        path: window.location.pathname,
        title: variant.seo.title,
        type: 'product',
        unified_name: `product-${variant.productCode}`,
      },
      _clear: true,
    });
  }, [variant.productCode, variant.seo.title]);

  // Did update variantUrl
  React.useEffect(() => {
    const trackPageView =
      !variant.canBuy ||
      additionalServices?.data?.CartServicesIncluded ||
      additionalServices?.error;

    const eventPayload = {
      variant,
      product,
      modifaceVariants,
      engravingAvailable:
        engravingAvailable ?? AdditionalServicesAvailability.notAvailable,
      tryItFirstAvailable:
        tryItFirstAvailable ?? AdditionalServicesAvailability.notAvailable,
      giftAvailable,
      loyaltyDiscount,
    };

    if (mounted.current) {
      trackItemInExponea(product, variant);
      trackPageReload({
        rrpShown,
        minimalPriceShown,
        hasWebcam: Boolean(hasWebcam),
        ...eventPayload,
      });
    } else if (!mounted.current && trackPageView) {
      trackItemInExponea(product, variant);

      // Detect webcam if there is at least one modiface variant
      // if hasWebcam, no need to detect it again
      if (Object.keys(modifaceVariants).length > 0 || hasWebcam) {
        detectWebcamAndTrackProductDetailView((trackerHasWebcam: boolean) =>
          // We need to use setTimeout for perf. optim. of hydration
          setTimeout(() => {
            trackPageReload({
              rrpShown,
              minimalPriceShown,
              hasWebcam: Boolean(trackerHasWebcam),
              ...eventPayload,
            });
          })
        );
      } else {
        trackPageReload({
          hasWebcam: Boolean(hasWebcam),
          rrpShown,
          minimalPriceShown,
          ...eventPayload,
        });
      }
      mounted.current = true;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match.params.variantUrl, additionalServices.loading]);

  const headerProps = {
    productId: product.id,
    productCode: product.code,
    collection: product.collection,
    subName: product.subName,
    brandInfo: product.brandInfo,
    desc: getVariantDescription(variant, product),
  };

  return (
    <>
      <ErrorBoundary>
        <HeadMeta product={product} variant={variant} />
      </ErrorBoundary>
      <ErrorBoundary>
        <PDViewPort
          newPdDesignEnabled={newPdDesignEnabled}
          // This attribute needs to be here due to exponea recommendations
          data-category={product.category}
          // This  attribute needs to be here due to deloitte
          data-product-code={variant.productCode}
        >
          <ProductJsonLd
            product={product}
            selectedVariant={variant}
            imageDomainPath={imageDomainPath}
          />

          {user.role === Roles.Admin && (
            <ErrorBoundary>
              <AdminPanel productId={product.id} productUrl={product.url} />
            </ErrorBoundary>
          )}

          <ContentWrapper newPdDesignEnabled={newPdDesignEnabled}>
            <Grid fluid={true}>
              <TopSection newPdDesign={newPdDesignEnabled}>
                <GalleryWrapper newPdDesignEnabled={newPdDesignEnabled}>
                  <BrandLogo
                    brandId={product.brandInfo.id}
                    brandUrl={product.brandInfo.url}
                  />
                  <ShowOnMobile newPdDesign={newPdDesignEnabled}>
                    <Breadcrumb items={product.breadcrumb} />
                    <Header
                      reviewsId="productBaseInfo-mobile"
                      {...headerProps}
                    />
                  </ShowOnMobile>
                  <ProductImageGallery
                    images={variant.imagesByCategory}
                    videos={variant.videos}
                    domain={imageDomainPath}
                    hasModiface={Boolean(modifaceVariants[variant.id])}
                    modifaceMobileEnabled={hasWebcam && modifaceMobileEnabled}
                    modifaceDesktopEnabled={hasWebcam && modifaceDesktopEnabled}
                    openModifaceModal={handleOpenModifaceModal}
                    isBrandLogoVisible={isLogoForBrandAvailable(
                      product.brandInfo.id
                    )}
                  />
                </GalleryWrapper>

                <MainInfoWrapper newPdDesignEnabled={newPdDesignEnabled}>
                  <ShowOnDesktop newPdDesign={newPdDesignEnabled}>
                    <Breadcrumb items={product.breadcrumb} />
                    <Header
                      reviewsId="productBaseInfo-desktop"
                      {...headerProps}
                    />
                  </ShowOnDesktop>

                  <ProductBaseInfo
                    brand={product.brand}
                    selectedVariant={variant}
                    product={product}
                    variants={product.variants}
                    currency={currentCurrency}
                    locale={currentLocale}
                    imageDomainPath={imageDomainPath}
                    deliveriesDisplayMode={
                      deliveriesDisplayMode as DeliveriesDisplayMode
                    }
                    variantName={
                      <VariantName
                        variant={variant}
                        unit={product.unit}
                        variantDisplayType={product.variantDisplayType}
                      />
                    }
                    hasWebcam={hasWebcam}
                    modifaceVariants={modifaceVariants}
                    engravingAvailable={engravingAvailable}
                    tryItFirstAvailable={tryItFirstAvailable}
                    giftAvailable={giftAvailable}
                  />
                </MainInfoWrapper>
              </TopSection>
            </Grid>

            <ProductTabContentGrid
              fluid={true}
              newPdDesignEnabled={newPdDesignEnabled}
            >
              <ProductTabContent
                reviewsOnServerIds={reviewsOnServerIds}
                product={product}
                variant={variant}
                rcKey={rcKey}
                customRcKey={customRcKey}
                customVersionKey={customVersionKey}
              />
            </ProductTabContentGrid>

            <Grid fluid={true}>
              <ErrorBoundary>
                <RenderInView>
                  <RoutineOrRelatedProducts
                    productId={product.id}
                    productCode={product.code}
                    relatedProducts={product.related.map((item) => item.id)}
                    databreakersIntegration={databreakersIntegration}
                  />
                </RenderInView>
              </ErrorBoundary>

              <ErrorBoundary>
                <RenderInView>
                  <LastVisitedProducts
                    productId={product.id}
                    variants={product.variants}
                  />
                </RenderInView>
              </ErrorBoundary>

              <ErrorBoundary>
                <RenderInView>
                  <BlogArticles
                    categorization={product.categorization}
                    brandId={product.brandId}
                    masterProductCode={product.code}
                  />
                </RenderInView>
              </ErrorBoundary>
            </Grid>
          </ContentWrapper>
        </PDViewPort>
      </ErrorBoundary>
    </>
  );
};

export const ProductDetail = (props: IPDProps) => (
  <ProductDetailContextProvider
    product={props.PDView.productDetailByMasterId[0]}
  >
    <TabSwitcherProvider>
      <ModalContext.Provider>
        <DrawerContext.Provider>
          <ProductDetailComponent {...props} />
        </DrawerContext.Provider>
      </ModalContext.Provider>
    </TabSwitcherProvider>
  </ProductDetailContextProvider>
);
